import { Link, NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  navigationDomainForPath,
  navigationDomains,
  type NavigationDomain,
  type NavigationItem
} from '@/components/layout/_navigation'
import { cn } from '@/lib/utils'

export default function Sidebar() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const activeDomain = navigationDomainForPath(pathname) ?? navigationDomains[0]

  return (
    <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-64 shrink-0 flex-col border-r border-border bg-card/70 md:flex">
      <nav aria-label={t('sidebar.primaryNavigation')} className="flex-1 overflow-y-auto px-3 py-5">
        <ul className="space-y-1">
          {navigationDomains.map((domain) => (
            <DomainLink
              key={domain.key}
              domain={domain}
              active={domain.key === activeDomain?.key}
              t={t}
            />
          ))}
        </ul>

        {activeDomain && activeDomain.key !== 'overview' && (
          <div className="mt-5 border-t border-border pt-4">
            <div className="px-3 pb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              {t(activeDomain.labelKey)}
            </div>
            <ul className="space-y-1">
              {activeDomain.items.map((item) => (
                <SidebarLink key={item.to} item={item} t={t} />
              ))}
            </ul>
          </div>
        )}
      </nav>
    </aside>
  )
}

function DomainLink({
  domain,
  active,
  t
}: {
  domain: NavigationDomain
  active: boolean
  t: (key: string) => string
}) {
  const Icon = domain.icon
  return (
    <li>
      <Link
        to={domain.to}
        data-active={active ? 'true' : undefined}
        className={cn(
          'flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors',
          active
            ? 'bg-secondary text-secondary-foreground'
            : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
        )}
      >
        <Icon className="size-4" />
        {t(domain.labelKey)}
      </Link>
    </li>
  )
}

function SidebarLink({ item, t }: { item: NavigationItem; t: (key: string) => string }) {
  const Icon = item.icon
  return (
    <li>
      <NavLink
        to={item.to}
        className={({ isActive }) =>
          cn(
            'flex h-9 items-center gap-3 rounded-md border-l-2 px-3 text-sm transition-colors',
            isActive
              ? 'border-primary bg-secondary text-secondary-foreground'
              : 'border-transparent text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
          )
        }
        end
      >
        <Icon className="size-4" />
        {t(item.labelKey)}
      </NavLink>
    </li>
  )
}
